// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@ces/XDL/types";
import color from "@ces/XDL/Utilities/color";
import { getSystemColor } from "@ces/XDL/Utilities/color";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

/**
 * Loading style: show 3 dots with animation
 * <Button>
 *   <StyledWrapper {...loadingButtonStyle} />
 * </Button>
 */
export const loadingButtonStyle = (loadingColor = "common.ui1") => {
    return {
        css: css`
            position: relative;
            left: -9999px;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(loadingColor)};
            color: ${color(loadingColor)};
            box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
            animation: dotPulse 1.5s infinite linear;
            animation-delay: 0.25s;

            &::before,
            &::after {
                content: "";
                display: inline-block;
                position: absolute;
                top: 0;
                width: 3px;
                height: 3px;
                border-radius: 5px;
                background-color: ${color(loadingColor)};
                color: ${color(loadingColor)};
            }

            &::before {
                box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseBefore 1.5s infinite linear;
                animation-delay: 0s;
            }

            &::after {
                box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseAfter 1.5s infinite linear;
                animation-delay: 0.5s;
            }

            @keyframes dotPulseBefore {
                0% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9984px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulse {
                0% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9999px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulseAfter {
                0% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 10014px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
            }
        `,
    };
};

export const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "common.ui7",
        hover: "common.ui8",
        text: "common.ui1",
        focus: "primary",
    },
    secondary: {
        main: "secondary",
        hover: "common.ui9",
        text: "text.main",
        focus: "common.ui10",
    },
    tertiary: {
        main: "common.ui7",
        hover: "common.ui8",
        text: "common.ui7",
        focus: "primary",
    },
};

export const GhostSecondaryButtonStyles = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;
    color: ${color(buttonColors.secondary.text)};
    border-color: ${color(buttonColors.secondary.main)};

    &:not(:disabled):hover {
        border-color: ${color(buttonColors.secondary.hover)};
        color: ${color(buttonColors.secondary.text, 0.33)};
    }
    &:not(:disabled):focus {
        border-color: ${color(buttonColors.secondary.focus)};
        color: ${color(buttonColors.secondary.text)};
    }
    &:disabled {
        border-color: ${getSystemColor(buttonColors.secondary.main, 0.33)};
        color: ${getSystemColor(buttonColors.secondary.text, 0.33)};
    }
`;

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        mergeCss: true,
        shape: "pill",
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            outline: none;

            &:not(:disabled):focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.main, 0.33)};
                border-color: transparent;
            }
        `,
    },
    secondary: {
        mergeCss: true,
        ...ButtonPresentationPropsDefault,
        buttonType: "solid",
        shape: "pill",
        hoverStyle: {
            color: `${getSystemColor(buttonColors.secondary.text, 0.33)}`,
            background: `${getSystemColor(buttonColors.secondary.hover)}`,
        },
        css: css`
            background: ${getSystemColor(buttonColors.secondary.main)};
            color: ${getSystemColor(buttonColors.secondary.text)};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            outline: none;
            border: none;
            &:not(:disabled):hover {
                color: ${getSystemColor(buttonColors.secondary.text, 0.33)};
            }
            &:not(:disabled):focus {
                color: ${getSystemColor(buttonColors.secondary.text)};
                background: ${getSystemColor(buttonColors.secondary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.secondary.main, 0.33)};
                color: ${getSystemColor(buttonColors.secondary.text, 0.33)};
            }
        `,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "outline",
        shape: "pill",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        css: css`
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-width: 1px;

            &:not(:disabled):hover {
                color: ${color(buttonColors.tertiary.hover)};
                border-color: ${color(buttonColors.tertiary.hover)};
            }
            &:not(:disabled):focus {
                outline: none;
                color: ${color(buttonColors.tertiary.focus)};
                border-color: ${color(buttonColors.tertiary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
                color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
            }
        `,
    },
} as xBaseTheme["button"];
